import { Grid, Stack, Table, Text, Title } from '@mantine/core';
import { IconCircleArrowDown, IconCircleArrowUp } from '@tabler/icons-react';

const abTestLegend = {
  averageOrderValueByGroup: 'Average Order Value',
  engagementIntervalsByGroupTotal: 'Page views tracked',
  productViewsByGroup: 'Product Views',
  pageViewsByGroup: 'Page Views',
  profilesByGroupTotal: 'Profiles',
  purchasesByGroup: 'Purchases',
  purchasesByGroupTotal: 'Purchases',
  revenueByGroup: 'Total revenue',
  // The two engagement metrics are means, not totals: the api sends the range-wide
  // mean per deduped page interval for these (helpers/ab-test-helper.js routes them
  // around the summing path, because summing a series of daily means is not a mean
  // of anything). Do not label these as sums or totals.
  scrollDepthByGroup: 'Scroll depth (%)',
  timeOnPageByGroup: 'Time on page (s)',
};

// The last column holds `conversionRate`, which for most metrics is a percentage
// rate — hence the default header and the `%` appended to the value. `revenueByGroup`
// keeps the inherited `%` suffix for byte-identical output with master even though
// the value is a currency mean, not a rate — that's a pre-existing quirk, not
// something this PR should touch. The two engagement metrics below are genuinely
// not rates: theirs is a mean in its own unit, so the header names the unit and
// the value carries no suffix.
const abTestRateColumn = {
  productViewsByGroup: { header: 'Rate', suffix: null },
  revenueByGroup: { header: 'Revenue per profile', suffix: '%' },
  scrollDepthByGroup: { header: 'Avg. scroll depth (%)', suffix: null },
  timeOnPageByGroup: { header: 'Avg. time on page (s)', suffix: null },
};

const defaultRateColumn = { header: 'Rate', suffix: '%' };

const AbTestTableView = ({ total, table, results, variables }) => {
  if (!total || !table || !results || !variables) {
    return null;
  }

  const tableControl = table.data.find((data) => data.id === 'control');
  const tableTreatment = table.data.find((data) => data.id === 'treatment');

  if (!tableControl || !tableTreatment) {
    return null;
  }

  const rateColumn = abTestRateColumn[table.name] || defaultRateColumn;

  let changeIndicator;
  if (results.change !== null) {
    changeIndicator =
      results.change.toFixed(2) > 0 ? (
        <IconCircleArrowUp size={14} style={{ color: 'var(--mantine-color-success-5)' }} />
      ) : (
        <IconCircleArrowDown size={14} style={{ color: 'var(--mantine-color-danger-5)' }} />
      );
  }

  const testChangeResults = (
    <Stack gap="xs">
      <Title order={3}>
        {results.change ? results.change.toFixed(2) : 'N/A'}{' '}
        <Text component="span" c="dimmed" size="lg">
          %
        </Text>{' '}
        {changeIndicator}
      </Title>
      <Text size="sm" c="dimmed" pt="xs" style={{ borderTop: '1px solid var(--mantine-color-primary-filled)' }}>
        Change
      </Text>
    </Stack>
  );

  const testSignificanceResults = (
    <Stack gap="xs">
      <Title order={3}>
        {(results.significance * 100).toFixed(2)}{' '}
        <Text component="span" c="dimmed" size="lg">
          %
        </Text>
      </Title>
      <Text size="sm" c="dimmed" pt="xs" style={{ borderTop: '1px solid var(--mantine-color-primary-filled)' }}>
        Statistical significance
      </Text>
    </Stack>
  );

  return (
    <Grid>
      <Grid.Col span={{ base: 12, sm: 4, lg: 3 }}>
        <Stack gap="xl">
          {testChangeResults}
          {testSignificanceResults}
        </Stack>
      </Grid.Col>
      <Grid.Col span="auto">
        <Table striped withTableBorder highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th></Table.Th>
              <Table.Th>{abTestLegend[total.name]}</Table.Th>
              <Table.Th>{abTestLegend[table.name]}</Table.Th>
              <Table.Th>{rateColumn.header}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td>Control group</Table.Td>
              <Table.Td>{total.data.control.toLocaleString('en-US') || null}</Table.Td>
              <Table.Td>{table.data.find((data) => data.id === 'control').value.toLocaleString('en-US')}</Table.Td>
              <Table.Td>
                {variables.variables.control.conversionRate
                  ? variables.variables.control.conversionRate.toLocaleString('en-US')
                  : 'N/A'}{' '}
                {rateColumn.suffix}
              </Table.Td>
            </Table.Tr>
            <Table.Tr>
              <Table.Td>Treatment group (With Releva)</Table.Td>
              <Table.Td>{total.data.treatment.toLocaleString('en-US') || null}</Table.Td>
              <Table.Td>{table.data.find((data) => data.id === 'treatment').value.toLocaleString('en-US')}</Table.Td>
              <Table.Td>
                {variables.variables.treatment.conversionRate
                  ? variables.variables.treatment.conversionRate.toLocaleString('en-US')
                  : 'N/A'}{' '}
                {rateColumn.suffix}
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
      </Grid.Col>
    </Grid>
  );
};

export default AbTestTableView;
